import * as zod from 'zod';

import { Step } from '@sb/remote-control/types';
import { ReplayTooltip } from '@sb/routine-runner/Step/RunDemonstration/ReplayTooltip';

export namespace RunDemonstrationStep {
  export const name = 'Run ShowPath';
  export const description =
    'Move the arm to a target pose, then replay a recorded demonstration.';
  export const isDecorator = false;
  export const librarySection = Step.LibrarySection.Control;
  export const librarySort = '7';
  export const argumentKind = 'RunDemonstration';
  export const featureFlag = 'demonstrationRecording';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    skillId: zod.string().optional(),
    recordingId: zod.string().optional(),
    positionListID: zod.string().nullable().default(null),
    positionListIndex: zod.number().optional(),
    repositionEnabled: zod.boolean().default(false),
    pin: zod.enum(['start', 'end', 'reference']).default('start'),
    tooltip: ReplayTooltip.optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    const {
      positionListID,
      positionListIndex,
      repositionEnabled,
      pin,
      tooltip,
      skillId,
      recordingId,
    } = args;

    const replayArgs = {
      target:
        repositionEnabled && positionListID
          ? { positionListID, positionListIndex }
          : null,
      repositionEnabled,
      pin,
      tooltip,
      // No FE controls for these; explicit null defers to the skill's shared
      // speed/trim bounds (or the recording's idle-trim) on the runner side.
      speed: null,
      trimStartTimestamp: null,
      trimEndTimestamp: null,
    };

    // The runner's Arguments is an either/or union: skill-driven steps emit
    // the skill id, and legacy steps emit just the recording id plus the
    // shared replay knobs. (A leftover recordingId on a skill step stays in
    // the FE configuration but is not forwarded to the runner.)
    if (skillId) {
      return {
        ...stepData,
        stepKind: 'RunDemonstration',
        args: {
          ...replayArgs,
          skillId,
        },
      };
    }

    if (!recordingId) {
      // validator() already blocks saving this state; guard for narrowing.
      throw new TypeError(
        'RunDemonstration step has neither a demonstration skill nor a recording',
      );
    }

    return {
      ...stepData,
      stepKind: 'RunDemonstration',
      args: { ...replayArgs, recordingId },
    };
  };
}

RunDemonstrationStep satisfies Step.StepKindInfo;
